import { useState, useRef, type ChangeEvent } from "react";
import {
  Upload,
  Camera,
  Crop as CropIcon,
  Trash2,
  Eye,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { ImageCropperModal, type CropAspectRatio } from "./ImageCropperModal";
import { ImageViewerModal } from "./ImageViewerModal";

export type ImageUploadShape = "circle" | "rounded" | "square";
export type ImageUploadSize = "sm" | "md" | "lg" | "xl";

export interface ImageUploadValue {
  file?: File;
  dataUrl: string;
}

export interface ImageUploadProps {
  value?: string | null;
  onChange?: (val: ImageUploadValue | null) => void;
  onRemove?: () => void;
  enableCrop?: boolean;
  cropAspectRatio?: CropAspectRatio;
  shape?: ImageUploadShape;
  size?: ImageUploadSize;
  disabled?: boolean;
  accept?: string;
  maxSizeBytes?: number;
  /** Show remove actions. Off when the server has no delete. */
  allowRemove?: boolean;
  objectFit?: "cover" | "contain";
  label?: string;
  hint?: string;
  error?: string;
  /** Centered vertical layout for modals and dialogs */
  centered?: boolean;
  /** Lock aspect ratio and hide the ratio selector (e.g. for avatar) */
  lockAspectRatio?: boolean;
  className?: string;
}

const sizeClasses: Record<ImageUploadSize, { box: string; icon: string }> = {
  sm: { box: "size-20", icon: "size-6" },
  md: { box: "size-28", icon: "size-8" },
  lg: { box: "size-36", icon: "size-10" },
  xl: { box: "size-48", icon: "size-12" },
};

const shapeClasses: Record<ImageUploadShape, string> = {
  circle: "rounded-full",
  rounded: "rounded-control",
  square: "rounded-none",
};

/**
 * ImageUpload Component - Dedicated image uploader with preview, crop modal, lightbox, and delete actions.
 */
export function ImageUpload({
  value,
  onChange,
  onRemove,
  enableCrop = true,
  cropAspectRatio = "1:1",
  shape = "rounded",
  size = "md",
  disabled = false,
  accept = "image/png,image/jpeg,image/webp,image/svg+xml",
  maxSizeBytes = 10 * 1024 * 1024, // 10MB
  allowRemove = true,
  objectFit = "cover",
  label,
  hint,
  error: customError,
  centered = false,
  lockAspectRatio = false,
  className,
}: ImageUploadProps) {
  const [internalError, setInternalError] = useState<string | null>(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [tempImageForCrop, setTempImageForCrop] = useState<string | null>(null);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayError = customError || internalError;
  const s = sizeClasses[size];

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInternalError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > maxSizeBytes) {
      setInternalError(
        `Kích thước ảnh (${(file.size / (1024 * 1024)).toFixed(1)} MB) vượt quá giới hạn ${(maxSizeBytes / (1024 * 1024)).toFixed(0)} MB.`
      );
      e.target.value = "";
      return;
    }

    setCurrentFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (enableCrop) {
        setTempImageForCrop(dataUrl);
        setCropperOpen(true);
      } else {
        onChange?.({ file, dataUrl });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropComplete = ({ blob, dataUrl }: { blob: Blob; dataUrl: string }) => {
    const fileName = currentFile?.name || "cropped_image.jpg";
    const file = new File([blob], fileName, { type: "image/jpeg" });
    onChange?.({ file, dataUrl });
  };

  const handleClear = () => {
    onRemove?.();
    onChange?.(null);
    setTempImageForCrop(null);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <span className="block text-caption font-semibold text-text">
          {label}
        </span>
      )}

      {/* Hidden input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={handleFileChange}
        className="sr-only"
        aria-hidden="true"
      />

      <div
        className={cn(
          "flex items-center gap-4",
          centered
            ? "flex-col justify-center text-center w-full"
            : "flex-wrap"
        )}
      >
        {/* Preview / Upload Box */}
        <div
          onClick={() => !disabled && !value && inputRef.current?.click()}
          className={cn(
            "group relative flex shrink-0 items-center justify-center overflow-hidden border-2 border-dashed bg-surface transition-all select-none",
            s.box,
            shapeClasses[shape],
            value
              ? "border-border/80 shadow-xs"
              : "border-border/80 hover:border-primary/60 hover:bg-bg/60 cursor-pointer",
            disabled && "opacity-50 cursor-not-allowed hover:border-border/80"
          )}
        >
          {value ? (
            <>
              <img
                src={value}
                alt="Preview"
                className={cn(
                  "size-full",
                  objectFit === "contain" ? "object-contain" : "object-cover",
                )}
              />

              {/* Action Overlay */}
              {!disabled && (
                <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/50 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  <IconButton
                    icon={<Eye className="size-4" />}
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewerOpen(true);
                    }}
                    label="Xem lớn"
                    className="text-white hover:bg-white/20"
                  />
                  {enableCrop && (
                    <IconButton
                      icon={<CropIcon className="size-4" />}
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTempImageForCrop(value);
                        setCropperOpen(true);
                      }}
                      label="Cắt lại ảnh"
                      className="text-white hover:bg-white/20"
                    />
                  )}
                  {allowRemove ? (
                    <IconButton
                      icon={<Trash2 className="size-4" />}
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClear();
                      }}
                      label="Xóa ảnh"
                      className="text-white hover:bg-danger/80"
                    />
                  ) : null}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center p-2 text-center text-text-muted">
              <Camera className={cn("text-primary/70 mb-1", s.icon)} />
              <span className="text-[11px] font-medium leading-tight">
                Tải ảnh
              </span>
            </div>
          )}
        </div>

        {/* Buttons / Actions side */}
        <div className={cn("flex flex-col gap-1.5", centered && "items-center text-center")}>
          <div className={cn("flex items-center gap-2", centered && "justify-center")}>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              {value ? "Đổi ảnh khác" : "Chọn ảnh"}
            </Button>

            {value && enableCrop && !disabled && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setTempImageForCrop(value);
                  setCropperOpen(true);
                }}
                className="flex items-center gap-1 text-caption"
              >
                <CropIcon className="size-3.5" />
                <span>Cắt ảnh</span>
              </Button>
            )}

            {value && allowRemove && !disabled && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleClear}
                className="text-caption text-danger hover:bg-danger-soft"
              >
                Xóa
              </Button>
            )}
          </div>

          <p className="m-0 text-caption text-text-muted">
            {hint || "Hỗ trợ PNG, JPEG, WEBP hoặc SVG (tối đa 10 MB)."}
          </p>
        </div>
      </div>

      {/* Error message */}
      {displayError && (
        <div className="flex items-center gap-1.5 text-caption font-semibold text-danger">
          <AlertCircle className="size-4 shrink-0" />
          <span>{displayError}</span>
        </div>
      )}

      {/* Image Cropper Modal */}
      {enableCrop && (
        <ImageCropperModal
          open={cropperOpen}
          onOpenChange={setCropperOpen}
          imageSrc={tempImageForCrop}
          aspectRatio={cropAspectRatio}
          lockAspectRatio={lockAspectRatio || shape === "circle" || cropAspectRatio === "1:1"}
          onCrop={handleCropComplete}
        />
      )}

      {/* Image Lightbox Viewer Modal */}
      {value && (
        <ImageViewerModal
          open={viewerOpen}
          onOpenChange={setViewerOpen}
          src={value}
          alt={label || "Hình ảnh"}
        />
      )}
    </div>
  );
}
